import { FadeIn, MaskedLine, Chapter } from "@/components/shared/Reveal";
import Triad from "@/components/shared/Triad";
import CTASection from "@/components/shared/CTASection";
import Marquee from "@/components/shared/Marquee";

const steps = [
  ["Discovery", "We walk the floor. Interviews, observation, and system archaeology — learning how work actually gets done, not how the org chart says it does."],
  ["Understanding", "The people who use the system every day hold the truth about it. We listen before we design, because adoption is decided here."],
  ["Evaluation", "The full technical canvas: existing capabilities worth reusing, platforms already licensed, build-versus-buy, and the real constraints of budget and skill."],
  ["Design", "Designer, Architect, and Developer converge. The solution is shaped by human need, existing assets, and technical reality — in that order."],
  ["Build", "Working software early and often. AI-assisted development and platform expertise compress timelines without compressing craft."],
  ["Prototype", "Real users touch real prototypes in days. We learn what slideware could never tell us."],
  ["Validation", "The people validate, the business validates, the architecture validates. Only then does the path forward become a plan."],
];

const Approach = () => (
  <main data-testid="approach-page" className="bg-ink">
    <section className="relative pt-40 pb-24 md:pb-32 overflow-hidden">
      <div className="absolute -top-32 right-1/4 w-[560px] h-[560px] rounded-full bg-amber-600/10 blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
        <MaskedLine delay={0.1}>
          <p className="label-mono">Our Approach</p>
        </MaskedLine>
        <h1 className="mt-8 font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-slate-100">
          <MaskedLine delay={0.25}>Human-Centered Design,</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="italic text-gold">practiced — not preached.</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.6}>
          <p className="mt-10 max-w-2xl text-lg md:text-xl text-slate-400 font-light leading-relaxed">
            Every solution we deliver is seen through three lenses before a line of code is
            written: the Designer who understands people, the Architect who reuses what exists,
            and the Developer who evaluates the full technical canvas.
          </p>
        </MaskedLine>
      </div>
    </section>

    <section className="relative border-t hairline bg-ink-light overflow-hidden">
      <div className="absolute top-1/3 left-1/2 w-[600px] h-[600px] -translate-x-1/2 rounded-full bg-amber-600/[0.06] blur-3xl pointer-events-none" />
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-36">
        <Triad />
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
          {[
            ["Designer", "Understand people", "Systems fail when they ignore the humans inside them. The Designer listens first — to frustrations, workarounds, and the workarounds of the workarounds."],
            ["Architect", "Reuse what exists", "You already own more capability than you realize. The Architect inventories platforms, licenses, and logic before proposing anything new."],
            ["Developer", "Evaluate the full canvas", "Every viable technology path — modern platform, custom build, integration, or retirement — is weighed against reality, not fashion."],
          ].map(([t, sub, b], i) => (
            <FadeIn
              key={t}
              delay={0.12 * i}
              className="border-l-2 border-amber-700/60 pl-6 py-2"
            >
              <p className="font-mono-tech text-amber-500/70 text-xs tracking-[0.3em] mb-3 uppercase">{sub}</p>
              <h3 className="font-serif text-2xl md:text-3xl text-slate-100 mb-4">{t}</h3>
              <p className="text-slate-400 font-light leading-relaxed text-sm">{b}</p>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <Marquee />

    <section className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
      <FadeIn className="mb-20 md:mb-28">
        <p className="label-mono mb-4">The Method, Chapter by Chapter</p>
        <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-slate-100 max-w-3xl leading-tight">
          Seven movements from legacy to modern.
        </h2>
      </FadeIn>
      <div className="space-y-20 md:space-y-28">
        {steps.map(([title, body], i) => (
          <Chapter key={title} number={`0${i + 1}`} title={title}>
            {body}
          </Chapter>
        ))}
      </div>
    </section>

    <CTASection
      title="Bring us your hardest legacy problem."
      sub="We will bring the Designer, the Architect, and the Developer — and the honesty to match."
    />
  </main>
);

export default Approach;
